import {Box, Switch, Text} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import {FILTER_PREDICATES_BY_MEASUREMENT_TYPE, MeasurementType} from 'samples/MeasurementForm';
import getTestProps from 'test/lib/cypress/getTestProps';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInputWithAction, {InlineAction} from 'ui-library/inputs/NumericInputWithAction';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {NumericInputChangeEvent} from 'util/Types';

import {
  ConcentrationSettings as ConcentrationSettingsType,
  ConcentrationSettingsWithDilutionSettings,
  DilutionSettings as DilutionSettingsType,
} from '../concentration-layers/machine/ConcentrationLayerMachine';
import DilutionSettings from './DilutionSettings';

export interface ConcentrationSettingsProps {
  /** The concentration settings to configure. */
  concentrationSettings: ConcentrationSettingsType;
  dilutionSettings: DilutionSettingsType;
  /** Callback function to update concentration settings */
  onUpdateConcentrationSettings: (settings: Partial<ConcentrationSettingsType>) => void;
  /** Callback function to update dilution settings */
  onUpdateLayerDilutionSettings: (settings: Partial<DilutionSettingsType>) => void;
  /** Optional inline action shown next to the concentration value input */
  inlineAction?: InlineAction;
}

/**
 * Determines if the current concentration settings are valid.
 *
 * The settings are invalid if any of the following are true:
 * - The concentration is not set
 * - The concentration unit is not set
 * - Dilution settings are enabled but:
 *   - The dilution factor is not set or is less than or equal to 0
 *   - The replicates are not set or are less than 1 or are not an integer
 *
 * @param settings - The concentration settings to check.
 * @returns True if the settings are valid, false otherwise.
 */
export function isConcentrationSettingsValid(settings: ConcentrationSettingsWithDilutionSettings): boolean {
  const {concentration, concentrationUnit, allowSetDilutionSettings, dilutionSettings} = settings;

  if (concentration == null || concentrationUnit == null) {
    return false;
  }
  if (allowSetDilutionSettings) {
    const {dilutionFactor, replicates} = dilutionSettings;
    if (dilutionFactor == null || dilutionFactor <= 0) {
      return false;
    }
    if (replicates == null || replicates < 1 || !Number.isInteger(replicates)) {
      return false;
    }
  }
  return true;
}

/**
 * Component for configuring concentration settings including units, concentration value,
 * and dilution settings toggle.
 */
export default function ConcentrationSettings({
  concentrationSettings,
  dilutionSettings,
  onUpdateConcentrationSettings,
  onUpdateLayerDilutionSettings,
  inlineAction,
}: ConcentrationSettingsProps): JSX.Element {
  // Local state for the concentration input - allows intermediate values like "." or ".5"
  // while the machine state only stores valid numbers
  const [localConcentration, setLocalConcentration] = useState<string | number>(
    concentrationSettings.concentration ?? ''
  );
  const concentrationInputId = useId();

  // Sync local state when machine state changes externally (e.g., switching layers)
  useEffect(() => {
    setLocalConcentration(concentrationSettings.concentration ?? '');
  }, [concentrationSettings.concentration]);

  const handleConcentrationChange = (e: NumericInputChangeEvent): void => {
    // Always update local state to allow smooth typing
    setLocalConcentration(e.target.value);

    // Keep machine state in sync with whether the current input is valid.
    // Invalid input must clear concentration so downstream validity checks
    // (like enabling/disabling the inline action) do not use stale values.
    if (e.target.numericValue != null) {
      onUpdateConcentrationSettings({
        concentration: e.target.numericValue,
      });
    } else {
      onUpdateConcentrationSettings({
        concentration: null,
      });
    }
  };

  return (
    <>
      <Box {...getTestProps({component: 'ConcentrationSettings', element: 'concentrationUnitDropdown'})}>
        <FieldLabel label="Units " isRequired={true} />
        <SearchableUnitDropdown
          selectedId={concentrationSettings.concentrationUnit?.id}
          unitTypeFilter={FILTER_PREDICATES_BY_MEASUREMENT_TYPE[MeasurementType.CONCENTRATION]}
          displaySelectionAsSymbol={true}
          onSelect={(unit) => {
            onUpdateConcentrationSettings({concentrationUnit: unit});
          }}
        />
        <Text size="sm" c="dimmed">
          Each layer can only have one unit.
        </Text>
      </Box>
      <Box {...getTestProps({component: 'ConcentrationSettings', element: 'concentrationValueInput'})}>
        <FieldLabel label="Concentration " isRequired={true} htmlFor={concentrationInputId} />
        {inlineAction != null ? (
          <NumericInputWithAction
            id={concentrationInputId}
            value={localConcentration}
            min={0}
            onChange={handleConcentrationChange}
            isDisabled={false}
            required={true}
            validateInteger={false}
            action={inlineAction}
            dataTestAttrs={{component: 'ConcentrationSettings', element: 'concentrationInput'}}
          />
        ) : (
          <Box {...getTestProps({component: 'ConcentrationSettings', element: 'concentrationInput'})}>
            <IntermediateValueControlledNumericInput
              id={concentrationInputId}
              value={localConcentration}
              min={0}
              onChange={handleConcentrationChange}
              isDisabled={false}
              required={true}
              validateInteger={false}
            />
          </Box>
        )}
      </Box>
      <Switch
        label="Set dilution"
        styles={{label: {fontWeight: 'bold'}}}
        checked={concentrationSettings.allowSetDilutionSettings}
        onChange={() => {
          onUpdateConcentrationSettings({
            allowSetDilutionSettings: !concentrationSettings.allowSetDilutionSettings,
          });
        }}
        {...getTestProps({component: 'ConcentrationSettings', element: 'dilutionSettingsToggle'})}
      />
      {concentrationSettings.allowSetDilutionSettings && (
        <DilutionSettings
          dilutionSettings={dilutionSettings}
          onUpdateLayerDilutionSettings={(settings: Partial<DilutionSettingsType>) => {
            onUpdateLayerDilutionSettings(settings);
          }}
        />
      )}
    </>
  );
}
